import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, Button, Spin, Tooltip, Typography, Divider, Input } from 'antd';
import { BiDownload } from 'react-icons/bi';
import { GetUsuarisIdAulaIdJutgeNotes } from '../../dao/GetUsuarisIdAulesIdJutgeNotes';
import { GetAulaRaConfig } from '../../dao/GetAulaRaConfig';
import { GetAlumneAulesId } from '../../dao/abp/GetAlumnesAulesId';
import { GetAlumnesValidacioByProjectV2 } from '../../dao/abp/GetAlumnesValidacioByProjectV2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface NotesResumModalProps {
    visible: boolean;
    onClose: () => void;
    aulaId: number;
    aulaNom: string;
    userId: string;
}

const FIXED_INSTRUMENTS = ['Actividades', 'Practicas', 'Evaluaciones', 'Prueba Practica'];

function normalizeRaName(rawRa: string): string {
    const s = String(rawRa).trim();
    // Matches "RA5", "RA 5", "RA-5", "5" and captures the number
    const match = s.match(/^(?:RA\s*-?\s*)?(\d+)/i);
    return match ? match[1] : s;
}

function computeJutgeRaGrades(
    userId: number,
    excelData: any,
    raConfig: any[]
): Record<string, number | null> {
    const result: Record<string, number | null> = {};
    if (!excelData) return result;

    const allRasRaw: string[] = (excelData.ras_aula || []).map((r: any) => r.nom);
    const mapNotas = new Map<string, any>(
        excelData.notes?.map((n: any) => [`${n.id_usuari}-${n.id_practica}`, n.nota]) || []
    );
    const mapNotasRa = new Map<string, any>(
        excelData.notes_ra?.map((n: any) => [`${n.id_usuari}-${n.id_ra}`, n.nota]) || []
    );

    for (const rawRa of allRasRaw) {
        const normRa = normalizeRaName(rawRa);
        const configItems = raConfig.filter((c: any) => normalizeRaName(c.nom_ra) === normRa);
        let raTotal = 0;
        let calculated = false;
        let failedInstrument = false;

        if (configItems.length > 0) {
            const totalWeight = configItems.filter(c => !c.isRecovery).reduce((sum, c) => sum + (c.pes || 0), 0);

            if (totalWeight === 0) {
                // Fallback: Check DB value first
                const dbVal = mapNotasRa.get(`${userId}-${rawRa}`);
                if (dbVal !== undefined) {
                    result[normRa] = dbVal;
                } else {
                    // Fallback: Simple average of all practices for this RA
                    const raPractices = excelData.practiques?.filter((p: any) =>
                        p.ras?.some((r: any) => r.nom === rawRa)
                    ) || [];
                    let sum = 0;
                    let count = 0;
                    raPractices.forEach((p: any) => {
                        const note = mapNotas.get(`${userId}-${p.id}`);
                        if (note !== null && note !== undefined) {
                            sum += Number(note);
                            count++;
                        }
                    });
                    result[normRa] = count > 0 ? (sum / count) / 10 : null;
                }
                continue;
            }

            const instGrades: Record<string, number | null> = {};
            configItems.forEach((conf: any) => {
                const inst = conf.instrument;
                const practices = excelData.practiques?.filter((p: any) =>
                    p.ras?.some((r: any) => r.nom === rawRa && r.instrument === inst)
                ) || [];

                let instAvg: number | null = null;
                if (practices.length > 0) {
                    const grades = practices.map((p: any) => mapNotas.get(`${userId}-${p.id}`)).filter((n: any) => n !== null && n !== undefined);
                    if (grades.length > 0) {
                        instAvg = (grades.reduce((acc: number, n: number) => acc + n, 0) / grades.length) / 10;
                    }
                }
                instGrades[inst] = instAvg;
            });

            const modifiedGrades = { ...instGrades };
            let finalGradeOverride: number | null = null;
            let finalGradeCapped = false;

            configItems.forEach((item: any) => {
                if (!item.isRecovery) return;
                let recGrade = instGrades[item.instrument];
                if (recGrade === null || recGrade === undefined || recGrade <= 0) return;
                if (recGrade > 10) recGrade /= 10;

                if (item.recoveryType === 'final_grade') {
                    const rawCap = item.notaMaxima;
                    const cap = (rawCap !== null && rawCap !== undefined && rawCap !== '') ? parseFloat(rawCap) : null;
                    let computedGrade = recGrade;

                    if (cap !== null && !isNaN(cap) && recGrade > cap) {
                        computedGrade = cap;
                    }

                    if (finalGradeOverride !== null && finalGradeOverride >= 5 && computedGrade < finalGradeOverride) {
                        // Mantener la nota si ya estaba aprobado con una recuperación anterior
                    } else {
                        finalGradeOverride = computedGrade;
                        finalGradeCapped = finalGradeOverride < (item.minim || 0);
                    }
                } else {
                    const target = item.targetInstrument || 'Prueba Practica';
                    const cur = modifiedGrades[target] ?? 0;
                    if (item.recoveryType === 'max') modifiedGrades[target] = Math.max(cur, recGrade);
                    else if (item.recoveryType === 'average') modifiedGrades[target] = (cur + recGrade) / 2;
                    else if (item.recoveryType === 'replace') modifiedGrades[target] = recGrade;
                }
            });

            configItems.forEach((conf: any) => {
                if (conf.isRecovery) return;
                const grade = modifiedGrades[conf.instrument];
                if (grade !== null && grade !== undefined) {
                    if (grade < (conf.minim || 0)) failedInstrument = true;
                    raTotal += grade * ((conf.pes || 0) / 100);
                    calculated = true;
                }
            });

            if (finalGradeOverride !== null && finalGradeOverride > raTotal) {
                raTotal = finalGradeOverride;
                failedInstrument = finalGradeCapped;
                calculated = true;
            }

            if (!calculated && finalGradeOverride === null) {
                const raPractices = excelData.practiques?.filter((p: any) =>
                    p.ras?.some((r: any) => r.nom === rawRa)
                ) || [];
                for (const p of raPractices) {
                    const note = mapNotas.get(`${userId}-${p.id}`);
                    if (note !== null && note !== undefined) {
                        calculated = true;
                        break;
                    }
                }
            }

        } else {
            // No config items -> Fallback logic
            const raPractices = excelData.practiques?.filter((p: any) => p.ras?.some((r: any) => r.nom === rawRa)) || [];
            const raInsts = Array.from(new Set(
                raPractices.flatMap((p: any) =>
                    p.ras.filter((r: any) => r.nom === rawRa).map((r: any) => r.instrument)
                )
            )).filter(Boolean) as string[];

            let tempTotal = 0;
            let instCount = 0;

            raInsts.forEach((inst) => {
                const practices = raPractices.filter((p: any) =>
                    p.ras?.some((r: any) => r.nom === rawRa && r.instrument === inst)
                );
                const grades = practices.map((p: any) => mapNotas.get(`${userId}-${p.id}`)).filter((n: any) => n !== null && n !== undefined);
                if (grades.length > 0) {
                    tempTotal += (grades.reduce((acc: number, n: number) => acc + n, 0) / grades.length) / 10;
                    instCount++;
                }
            });

            const dbVal = mapNotasRa.get(`${userId}-${rawRa}`);
            if (dbVal !== undefined) {
                raTotal = dbVal;
                calculated = true;
            } else if (instCount > 0) {
                raTotal = tempTotal / instCount;
                calculated = true;
            }
        }

        if (calculated && failedInstrument && raTotal > 4) {
            raTotal = 4;
        }

        result[normRa] = calculated ? Math.round(raTotal * 100) / 100 : null;
    }

    return result;
}

/** Compute ABP RA grades per student from validacio data */
function computeAbpRaGrades(validacioData: any[]): Record<string, number | null> {
    const result: Record<string, number | null> = {};
    if (!validacioData) return result;

    for (const item of validacioData) {
        const rawRa = item.ranom || item.ra;
        if (!rawRa) continue;
        
        // Extract the RA number so it matches the Jutge formatting (e.g. "RA5: Desc" -> "5")
        const normRa = normalizeRaName(String(rawRa));

        const hasEval = item.avaluacio !== null && item.avaluacio !== undefined && Number(item.avaluacio) !== 0;
        const nota = hasEval && item.nota !== null && item.nota !== undefined && item.nota !== ""
            ? Number(item.nota)
            : 0;

        result[normRa] = Math.round(nota * 100) / 100;
    }
    return result;
}

const NotesResumModal: React.FC<NotesResumModalProps> = ({ visible, onClose, aulaId, aulaNom, userId }) => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<any[]>([]);
    const [allRas, setAllRas] = useState<string[]>([]);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        if (visible && aulaId) loadData();
    }, [visible, aulaId]);

    async function loadData() {
        setLoading(true);
        try {
            const [jutgeData, raConfig, alumnesAbp] = await Promise.all([
                GetUsuarisIdAulaIdJutgeNotes(userId, aulaId + ''),
                GetAulaRaConfig(aulaId + ''),
                GetAlumneAulesId(aulaId + '')
            ]);

            // -- Jutge side --
            const jutgeStudents: Map<number, string> = new Map();
            const jutgeRaGrades: Map<number, Record<string, number | null>> = new Map();

            if (jutgeData && Array.isArray(jutgeData.notes)) {
                const allJutgeRas: string[] = (jutgeData.ras_aula || []).map((r: any) => normalizeRaName(r.nom));
                const uniqueUsers = Array.from(new Set(jutgeData.notes.map((n: any) => n.id_usuari))) as number[];

                for (const uid of uniqueUsers) {
                    const alumne = jutgeData.alumnes?.find((a: any) => a.id === uid);
                    if (alumne) jutgeStudents.set(uid, `${alumne.nom} ${alumne.cognoms || ''}`.trim());
                    jutgeRaGrades.set(uid, computeJutgeRaGrades(uid, jutgeData, raConfig || []));
                }

                setAllRas(prev => {
                    const merged = Array.from(new Set([...prev, ...allJutgeRas]));
                    return merged.sort();
                });
            }

            // -- ABP side --
            const abpRaGrades: Map<number, Record<string, number | null>> = new Map();
            const abpRaSet = new Set<string>();

            for (const alumne of (alumnesAbp || [])) {
                try {
                    const val = await GetAlumnesValidacioByProjectV2('0', alumne.id + '', -2);
                    if (val && val.length > 0) {
                        const grades = computeAbpRaGrades(val);
                        abpRaGrades.set(alumne.id, grades);
                        Object.keys(grades).forEach(r => abpRaSet.add(r));
                    }
                } catch { }
            }

            // -- Merge --
            const allRasUnion = Array.from(new Set([
                ...(jutgeData?.ras_aula ? jutgeData.ras_aula.map((r: any) => normalizeRaName(r.nom)) : []),
                ...Array.from(abpRaSet)
            ])).sort((a: any, b: any) => {
                const numA = parseInt(a);
                const numB = parseInt(b);
                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                return a.localeCompare(b);
            });
            setAllRas(allRasUnion);

            // Build combined rows keyed by student id (using strings to avoid mismatch)
            const studentMap = new Map<string, any>();

            // From jutge
            jutgeRaGrades.forEach((grades, uid) => {
                const strId = String(uid);
                const name = jutgeStudents.get(uid) || `User ${strId}`;
                studentMap.set(strId, { id: strId, nom: name, jutge: grades, abp: {} });
            });

            // From ABP
            abpRaGrades.forEach((grades, uid) => {
                const strId = String(uid);
                const alumne = alumnesAbp?.find((a: any) => String(a.id) === strId);
                const name = alumne ? `${alumne.nom} ${alumne.cognoms || ''}`.trim() : `User ${strId}`;
                if (studentMap.has(strId)) {
                    studentMap.get(strId)!.abp = grades;
                } else {
                    studentMap.set(strId, { id: strId, nom: name, jutge: {}, abp: grades });
                }
            });

            // Compute merged final grades
            const finalRows = Array.from(studentMap.values()).map(student => {
                const merged: Record<string, number | null> = {};
                for (const ra of allRasUnion) {
                    const j = student.jutge[ra] ?? null;
                    const a = student.abp[ra] ?? null;
                    if (j !== null && a !== null) {
                        merged[ra] = Math.round(((j + a) / 2) * 100) / 100; // media (no debería pasar)
                    } else {
                        merged[ra] = j ?? a;
                    }
                }
                return { ...student, merged };
            });

            finalRows.sort((a, b) => a.nom.localeCompare(b.nom));
            setRows(finalRows);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }

    const gradeTag = (val: number | null) => {
        if (val === null) return <Tag color="default" style={{ margin: 0 }}>N/P</Tag>;
        const rounded = val < 5 ? Math.floor(val) : Math.round(val);
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <Tag color={val >= 5 ? 'success' : 'error'} style={{ margin: 0, minWidth: 38, textAlign: 'center', fontWeight: 600, padding: '0 4px', fontSize: 12 }}>
                    {val.toFixed(2)}
                </Tag>
                <Tag color={rounded >= 5 ? 'success' : 'error'} style={{ margin: 0, minWidth: 26, textAlign: 'center', fontWeight: 800, fontSize: 13, padding: '0 4px' }}>
                    {rounded}
                </Tag>
            </div>
        );
    };

    const raHeaderColors = ['#389e0d', '#d46b08', '#096dd9', '#c41d7f', '#08979c', '#7c3aed'];
    const raBgColors = ['#f6ffed', '#fffbe6', '#e6f7ff', '#fff0f6', '#e6fffb', '#f5f0ff'];

    const columns: any[] = [
        {
            title: <span style={{ fontWeight: 700 }}>Alumne</span>,
            dataIndex: 'nom',
            key: 'nom',
            fixed: 'left',
            width: 140,
            render: (v: string) => <span style={{ fontWeight: 600, fontSize: 12 }}>{v}</span>
        },
        ...allRas.map((ra, idx) => ({
            title: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                    <span style={{ fontWeight: 800, color: raHeaderColors[idx % raHeaderColors.length], fontSize: 12 }}>RA {ra}</span>
                </div>
            ),
            key: `ra_${ra}`,
            align: 'center' as const,
            width: 80,
            onHeaderCell: () => ({ style: { background: raBgColors[idx % raBgColors.length] } }),
            onCell: () => ({ style: { background: raBgColors[idx % raBgColors.length] } }),
            render: (_: any, record: any) => gradeTag(record.merged[ra] ?? null)
        }))
    ];

    const downloadExcel = async () => {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Notas Combinades');

        const headers = ['Alumne', ...allRas.map(ra => `RA ${ra}`), ...allRas.map(ra => `RA ${ra} (Fin)`)];
        ws.addRow(headers);
        ws.getRow(1).font = { bold: true };

        rows.forEach(row => {
            const cells = [
                row.nom,
                ...allRas.map(ra => row.merged[ra] ?? ''),
                ...allRas.map(ra => row.merged[ra] !== null ? (row.merged[ra] < 5 ? Math.floor(row.merged[ra]) : Math.round(row.merged[ra])) : '')
            ];
            ws.addRow(cells);
        });

        ws.columns.forEach(col => { col.width = 14; });
        ws.getColumn(1).width = 24;

        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `notes_combinades_aula_${aulaId}.xlsx`);
    };

    const filteredRows = rows.filter(r => r.nom.toLowerCase().includes(searchText.toLowerCase()));

    return (
        <Modal
            maskClosable={false}
            title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 40 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>📊 Resum de Notas — {aulaNom}</span>
                    <Button icon={<BiDownload />} type="primary" size="small" onClick={downloadExcel}>
                        Exportar Excel
                    </Button>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={Math.min(250 + allRas.length * 85, 1200)}
            styles={{ body: { padding: '16px 0', maxHeight: '75vh', overflowY: 'auto' } }}
        >
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                    <Spin size="large" tip="Carregant dades..." />
                </div>
            ) : (
                <>
                    <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Combina les notes de <strong>Jutge/Pràctiques</strong> i <strong>ABP/Projectes</strong> per RA.
                            Si el mateix RA apareix a les dues seccions es calcula la mitjana (no hauria de passar).
                        </Typography.Text>
                        <Input.Search
                            placeholder="Cercar alumne pel nom..."
                            allowClear
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: 300 }}
                        />
                    </div>
                    <Table
                        bordered
                        size="small"
                        columns={columns}
                        dataSource={filteredRows}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                </>
            )}
        </Modal>
    );
};

export default NotesResumModal;
